import { ckmeans } from 'simple-statistics';
import { isFinite } from 'lodash/fp';

/**
 * Break up a dataset into bucket values
 * @param   {number}   buckets  number of buckets to split up
 * @param   {array}    data     data to split up, either an array of numbers or an array of objects
 * @param   {function} accessFn optional callback for finding a value if data is an array of objects
 * @returns {array}             an array of array of bucket values
 */
export function breakData(buckets, data, accessFn = false) {
  // If there is a callback, apply it.
  const values = accessFn ? data.map(accessFn) : data;

  // If there are no values, return an empty array.
  if (!Array.isArray(values) || 0 === values.length) {
    return [];
  }

  return ckmeans(values, Math.min(buckets, values.length));
}

/**
 * map break data result into an object of buckets with min/maxes
 * @param   {array} buckets region values
 * @returns {object}        region definitions
 */
export function mapBuckets(buckets) {
  return buckets.map((bucket, idx) => ({
    idx,
    min: Math.min(...bucket),
    max: Math.max(...bucket),
    count: bucket.length,
    values: bucket,
  }));
}

/**
 * organize data and set buckets
 * @param   {number}   buckets          number of buckets to split up
 * @param   {array}    data             data to split up, either an array of numbers or an array of objects
 * @param   {bool}     includeBuckets   include the bucket definitions
 * @returns {array}                     region definitions
 */
export function organizeData(bucketCount, data, includeBuckets = false) {
  const buckets = mapBuckets(
    breakData(
      bucketCount,
      data.filter(({ value }) => isFinite(value)),
      ({ value }) => value
    )
  );

  const retData = data.map(({ value, ...datum }) => {
    const bucket = buckets
      .find(({ min, max }) => (min <= value && max >= value));
    return {
      ...datum,
      value,
      bucket: bucket ? bucket.idx : 'NA',
    };
  });

  const formattedBuckets = buckets.map(({ min, max, ...bucket }) => {
    const maxItem = retData.find(({ value }) => value === min);
    const minItem = retData.find(({ value }) => value === max);
    return ({
      ...bucket,
      min: minItem ? minItem.formattedValue : min,
      max: maxItem ? maxItem.formattedValue : max,
    });
  });

  if (includeBuckets) {
    return {
      buckets: formattedBuckets,
      data: retData,
    };
  }

  return retData;
}

export default {
  breakData,
  mapBuckets,
  organizeData,
};



// WEBPACK FOOTER //
// ./app/services/breakAndOrganizeData.js